import { useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useNotifications = (token) => {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    // Check if notifications are supported
    const supported = 'Notification' in window && 'serviceWorker' in navigator;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service Worker not supported');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.warn('Notifications not supported');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        await registerServiceWorker();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [isSupported, registerServiceWorker]);

  // Send a local notification (for testing or immediate notifications)
  const sendLocalNotification = useCallback((title, options = {}) => {
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    const defaultOptions = {
      icon: '/favicon-192x192.png',
      badge: '/favicon-32x32.png',
      vibrate: [200, 100, 200],
      ...options
    };

    // Try using service worker first
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, defaultOptions);
      });
    } else {
      // Fallback to regular notification
      new Notification(title, defaultOptions);
    }
  }, [permission]);

  // Notification templates for different events
  const notifyPackageReceived = useCallback((trackingNumber, description) => {
    sendLocalNotification('📦 Paquete Recibido', {
      body: `Tu paquete ${trackingNumber} ha sido recibido en el almacén.\n${description || ''}`,
      tag: `package-received-${trackingNumber}`,
      data: { url: '/', type: 'package_received', tracking: trackingNumber }
    });
  }, [sendLocalNotification]);

  const notifyStatusChange = useCallback((trackingNumber, newStatus, statusLabel) => {
    const statusMessages = {
      'received': '📦 Recibido en almacén',
      'in_transit': '🚚 En tránsito',
      'delivered': '✅ Entregado',
      'in_repack': '📋 En proceso de reempaque',
      'pending': '⏳ Pendiente'
    };

    const icon = statusMessages[newStatus] || '📦';
    
    sendLocalNotification(`${icon} Estado Actualizado`, {
      body: `Tu paquete ${trackingNumber} ahora está: ${statusLabel || newStatus}`,
      tag: `status-${trackingNumber}-${newStatus}`,
      data: { url: '/', type: 'status_change', tracking: trackingNumber, status: newStatus }
    });
  }, [sendLocalNotification]);

  const notifyRepackUpdate = useCallback((repackId, status, message) => {
    const statusIcons = {
      'approved': '✅',
      'rejected': '❌',
      'processing': '🔄',
      'completed': '🎉'
    };

    sendLocalNotification(`${statusIcons[status] || '📋'} Reempaque Actualizado`, {
      body: message || `Tu solicitud de reempaque ha sido actualizada a: ${status}`,
      tag: `repack-${repackId}`,
      data: { url: '/', type: 'repack_update', repackId }
    });
  }, [sendLocalNotification]);

  const notifyNewInvoice = useCallback((invoiceNumber, amount) => {
    sendLocalNotification('🧾 Nueva Factura', {
      body: `Se ha generado la factura ${invoiceNumber} por $${amount}`,
      tag: `invoice-${invoiceNumber}`,
      data: { url: '/', type: 'new_invoice', invoiceNumber }
    });
  }, [sendLocalNotification]);

  return {
    permission,
    isSupported,
    subscription,
    requestPermission,
    sendLocalNotification,
    notifyPackageReceived,
    notifyStatusChange,
    notifyRepackUpdate,
    notifyNewInvoice
  };
};

export default useNotifications;
